import React, { useContext, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import { useSearchParams, useParams } from "../../hooks/useNavigation";
import { readableColor } from "polished";
import PropTypes from "prop-types";
import styled, { ThemeContext } from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import useIsHydrated from "../../hooks/useIsHydrated";
import CustomSelect from "../../CustomSelect/CustomSelect";
import Icon from "../../Icon/Icon";
import useDevice from "../../customHooks/useDevice";
// Search is now handled by filtersStore.search()
import Markdown from "../../styles/Markdown";
import Loader from "../Loader/Loader";
import FrontendFilter from "./GridFilters/FrontendFilter";
import KeywordWrapper from "./GridFilters/KeywordFilter";
import LocationFilter from "./GridFilters/LocationFilter";
import GridVacancyList from "./GridVacancyList";
import NoVacancies from "./NoVacancies";
import StyledPagination from "./StyledPagination";

const ITEMS_PER_PAGE = 12;

const StyledContainer = styled.div`
  transition: max-height 0.5s ease-in-out, opacity 0.5s ease-in-out;
  max-height: ${(props) => (props.isHidden ? "0" : "1500px")};
  overflow: hidden;
  flex-direction: column;
  width: ${(props) => (props.isMobile ? "100%" : "auto")};
  margin-top: ${(props) => (props.isMobile ? "0" : "2rem")};
  margin-bottom: ${(props) => (props.isMobile ? "0" : "2rem")};
`;

const StyledFiltersContainer = styled.div`
  background-color: ${({ theme }) => theme.primaryBackgroundColor};
  height: 100%;
  color: white;
  padding: 1rem;
`;

const StyledFilters = styled.div`
  outline: none;
  flex-grow: 1;
  display: flex;
  justify-content: left;
  height: 100%;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
  margin-bottom: auto;
  span {
    margin-top: auto;
    margin-bottom: auto;
  }
`;

const StyledLink = styled.div`
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  text-decoration: underline;
  cursor: pointer;
`;

const StyledLinkWrapper = styled.div`
  padding-right: 1rem;
  margin-top: auto;
  margin-bottom: auto;
  min-width: 8rem;
`;

const StyledSelect = styled.div`
  border-left: 1px solid #ccc;
  padding-left: 2rem;
  color: black;
  height: 100%;
  min-width: 12rem;
`;

const StyledBox = styled.div`
  padding: 5px 10px;
  margin-left: 10px;
  display: inline-block;
  width: auto;
  background-color: #393e44;
  text-transform: uppercase;
`;

const StyledFiltersDisplay = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.primaryBackgroundColor};
  padding: 1.5rem;
  justify-content: space-between;
  gap: 1.5rem;
`;

const StyledShowFilters = styled.div`
  border: 1px solid #fff !important;
  flex-basis: 50%;
  span {
    color: #fff;
    text-transform: uppercase;
    font-size: 15px;
    font-weight: 600;
    display: block;
    padding: 20px 56px 20px 20px;
    position: relative;
  }
`;

const StyledMobileSelect = styled.div`
  background-color: #18191c;
  flex-basis: 50%;
`;

function transformFiltersToArray(filters) {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      value.forEach((v) => {
        acc.push({ label: `${key}: ${v}`, type: key, value: v });
      });
    }
    return acc;
  }, []);
}

function GridSearchResults(props) {
  const { isLoading } = props;

  const {
    additionalConfig,
    displayVancanciesCount = true,
    searchIconColour,
    descriptionText,
    itemsPerPage,
    jobCardType = "JobCardWithTitle",
  } = useContext(ComponentContext);
  const { isSmallScreen, isMobile } = useDevice();
  const { filtersStore } = useStores();
  const [showFilters, setShowFilters] = useState(false);
  const {
    searchResults,
    resetAllFilters,
    frontendFilters,
    pagination,
    isFetching,
    removeFromFilter,
    setFrontendFiltersFromQuery,
  } = filtersStore;

  const itemsToDisplay = itemsPerPage || ITEMS_PER_PAGE;
  const theme = useContext(ThemeContext);
  const [paginatedSearchResults, setPaginatedSearchResults] =
    useState(searchResults);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [resultsCount, setResultsCount] = useState(0);
  const [resultsOrder, setResultsOrder] = useState("DESC");

  const params = useParams();
  const searchParams = useSearchParams();
  const searchObj = Object.fromEntries(searchParams.entries());
  const combindedParms = { ...params, ...searchObj };

  const isHydrated = useIsHydrated();
  const isServer = !isHydrated;

  const filteredSearchResults = searchResults.filter((result) => {
    if (
      isEmpty(frontendFilters.hours) &&
      isEmpty(frontendFilters.country) &&
      isEmpty(frontendFilters.role)
    ) {
      return true;
    }

    let passesCategoryFilter = isEmpty(frontendFilters.role);
    let passesHoursFilter = isEmpty(frontendFilters.hours);
    let passesCountryFilter = isEmpty(frontendFilters.country);

    // Check category filter
    if (!passesCategoryFilter) {
      passesCategoryFilter = frontendFilters.role.includes(result.category);
    }

    // Check hours filter
    if (!passesHoursFilter) {
      passesHoursFilter = frontendFilters.hours.includes(result.hoursType);
    }

    // Check country filter
    if (!passesCountryFilter) {
      passesCountryFilter = frontendFilters.country.includes(
        result.location.country
      );
    }

    // Result must pass all non-empty filter groups
    return passesCategoryFilter && passesHoursFilter && passesCountryFilter;
  });

  useEffect(() => {
    const endOffset = currentPage + itemsToDisplay;

    setPaginatedSearchResults(
      filteredSearchResults.slice(currentPage, endOffset)
    );
    setPageCount(Math.ceil(filteredSearchResults.length / itemsToDisplay));
    setResultsCount(filteredSearchResults.length);
  }, [currentPage, itemsToDisplay, JSON.stringify(filteredSearchResults)]);

  const search = () => {
    filtersStore.search({ order: resultsOrder });
    setCurrentPage(0);
  };

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) return;
    search();
  }, [resultsOrder]);

  const searchParamsString = searchParams.toString();

  useEffect(() => {
    setFrontendFiltersFromQuery(combindedParms);
    filtersStore.search({ order: resultsOrder }).then(() => {
      hasMounted.current = true;
    });
    setCurrentPage(0);
  }, [searchParamsString]);

  const handlePageClick = (event) => {
    const newOffset =
      (event.selected * itemsToDisplay) % pagination.totalResults;
    setCurrentPage(newOffset);
  };

  const listClassName = classNames("col container-xxl", {
    "col-md-8 col-lg-9":
      !additionalConfig?.disableRoleFilter && !(isMobile || isSmallScreen),
  });

  const roleSelectStyles = {
    container: (provided) => {
      return {
        ...provided,
        height: "100%",
        width: "100%",
      };
    },
    control: (provided) => ({
      ...provided,
      background: theme.primaryBackgroundColor,
      borderRadius: "none",
      boxShadow: "none",
      fontWeight: "400",
      height: "100%",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "0",
    }),
    option: (provided) => ({
      ...provided,
      background: theme.primaryBackgroundColor,
      color: readableColor(
        theme.primaryBackgroundColor,
        theme.textColor,
        theme.negativeTextColor,
        false
      ),
      height: "100%",
    }),
    placeholder: (provided) => ({
      ...provided,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: readableColor(
        theme.primaryBackgroundColor,
        theme.textColor,
        theme.negativeTextColor,
        false
      ),
    }),
  };

  const content = (
    <>
      <div className="mb-5 mt-5">
        <GridVacancyList
          displayVancanciesCount={displayVancanciesCount}
          isLoading={isFetching}
          resultsCount={resultsCount}
          searchResults={paginatedSearchResults}
          type={jobCardType}
        />
      </div>
      {resultsCount > itemsToDisplay ? (
        <StyledPagination
          handlePageClick={handlePageClick}
          pageCount={pageCount}
        />
      ) : null}
    </>
  );

  const isEmptyResults = isEmpty(paginatedSearchResults);

  const sortButton = (
    <CustomSelect
      defaultValue={{ label: "Latest", value: "DESC" }}
      id="role"
      onChange={(ord) => {
        setResultsOrder(ord.value);
      }}
      options={[
        { label: "Oldest", value: "ASC" },
        { label: "Latest", value: "DESC" },
      ]}
      styles={roleSelectStyles}
      theme={(a) => ({
        ...a,
        background: theme.primaryBackgroundColor,
        colors: {
          ...theme.colors,
          primary: readableColor(
            theme.primaryBackgroundColor,
            theme.textColor,
            theme.negativeTextColor,
            false
          ),
        },
      })}
    />
  );

  const filterArray = transformFiltersToArray(frontendFilters);

  return (
    <div>
      {isMobile || isSmallScreen ? null : (
        <>
          <StyledFiltersContainer className="d-flex">
            <div className="container-xxl m-auto d-flex">
              <StyledFilters>
                <span>
                  Filtered by: <Icon icon="chevronRight" size={16} />
                </span>
                {filterArray.map((filter) => (
                  <StyledBox
                    key={filter.value}
                    onClick={() => {
                      removeFromFilter(filter.type, filter.value);
                      search();
                    }}
                  >
                    {filter.label} <Icon icon="clearInput" size={16} />
                  </StyledBox>
                ))}
              </StyledFilters>
              <div className="d-flex">
                <StyledLinkWrapper>
                  <StyledLink onClick={() => resetAllFilters()}>
                    Reset Filters
                  </StyledLink>
                </StyledLinkWrapper>
                <StyledSelect>{sortButton}</StyledSelect>
              </div>
            </div>
          </StyledFiltersContainer>
          <Markdown>{descriptionText}</Markdown>
        </>
      )}
      <div
        className={classNames(" m-auto", {
          "container-xxl row gx-5": !(isMobile || isSmallScreen),
        })}
      >
        <div
          className={classNames("display-flex flex-direction-column gap-2", {
            "col-md-4 col-lg-3": !(isMobile || isSmallScreen),
          })}
        >
          {isMobile || isSmallScreen ? (
            <StyledFiltersDisplay>
              <StyledShowFilters onClick={() => setShowFilters(!showFilters)}>
                <span>{showFilters ? "Hide" : "Show"} Filters</span>
              </StyledShowFilters>
              <StyledMobileSelect>{sortButton}</StyledMobileSelect>
            </StyledFiltersDisplay>
          ) : null}
          <StyledContainer
            isHidden={(isMobile || isSmallScreen) && !showFilters}
            isMobile={isMobile || isSmallScreen}
          >
            <KeywordWrapper search={search} />
            <LocationFilter
              initialValue={combindedParms}
              isLoading={isFetching || isServer}
              search={search}
              searchIconColour={searchIconColour}
            />
            <FrontendFilter
              isLoading={isFetching || isServer}
              label="Country"
              type="country"
            />

            <FrontendFilter
              isLoading={isFetching || isServer}
              label="Hours"
              type="hours"
            />
            <FrontendFilter
              isLoading={isFetching || isServer}
              label="Role"
              type="role"
            />
          </StyledContainer>
        </div>
        {isMobile || isSmallScreen ? (
          <Markdown>{descriptionText}</Markdown>
        ) : null}
        <div className={listClassName}>
          {isLoading && <Loader minHeight="450px" />}
          {isEmptyResults && <NoVacancies />}
          {!isLoading && !isFetching && !isEmptyResults && content}
        </div>
      </div>
    </div>
  );
}

export default observer(GridSearchResults);

GridSearchResults.propTypes = {
  isLoading: PropTypes.bool,
};

GridSearchResults.defaultProps = {
  isLoading: false,
};
